import type { ReactNode } from "react";
import type { FlightKey } from "@b2bportal/air-price-watch-api";
import { useModuleBEM } from "@b2bportal/core-themes";
import {
  Dialog,
  DialogContent,
  type DialogProps,
  DialogTrigger,
} from "@b2bportal/core-ui/overridable";
import { useI18nContext } from "@hopper-b2b/i18n";

import {
  useWatchTripDialog,
  WatchTripDialogView,
} from "./hooks/useWatchTripDialog";
import styles from "./styles.module.scss";

import { UnwatchTripView } from "./UnwatchTripView";
import { WatchTripFailure } from "./WatchTripFailure";
import { WatchTripForm } from "./WatchTripForm";
import { WatchTripSuccess } from "./WatchTripSuccess";

export interface WatchTripDialogProps extends DialogProps {
  className?: string;
  flightKey: FlightKey;
  onSuccessClose?: () => void;
}

export const WatchTripDialog = ({
  className,
  open,
  onClose: propsOnClose,
  onOpen,
  children,
  flightKey,
  onSuccessClose,
}: WatchTripDialogProps): JSX.Element => {
  const { t } = useI18nContext();

  const [block, cn] = useModuleBEM(styles, "WatchTripDialog");

  const {
    context: { currentView },
    handlers: { onClose, navigate },
  } = useWatchTripDialog({
    open,
    onClose: propsOnClose,
  });

  // Display correct view
  let content: ReactNode = null;

  switch (currentView) {
    case WatchTripDialogView.WATCH:
      content = (
        <WatchTripForm
          cn={cn}
          flightKey={flightKey}
          onSuccess={() => {
            navigate(WatchTripDialogView.WATCH_SUCCESS);
          }}
          onError={() => {
            navigate(WatchTripDialogView.FAILURE);
          }}
        />
      );
      break;
    case WatchTripDialogView.WATCH_SUCCESS:
      content = (
        <WatchTripSuccess
          cn={cn}
          onClose={() => {
            onSuccessClose?.();
            onClose?.();
          }}
        />
      );
      break;
    case WatchTripDialogView.UNWATCH:
      content = (
        <UnwatchTripView
          cn={cn}
          flightKey={flightKey}
          onSuccess={onClose}
          onError={() => {
            navigate(WatchTripDialogView.FAILURE);
          }}
        />
      );
      break;
    case WatchTripDialogView.FAILURE:
      content = <WatchTripFailure cn={cn} onClose={onClose} />;
      break;
  }

  return (
    <Dialog open={open} onClose={onClose} onOpen={onOpen}>
      <DialogTrigger>
        <button className={className}>{children}</button>
      </DialogTrigger>

      <DialogContent className={block} title={t("priceWatch.title")}>
        {content}
      </DialogContent>
    </Dialog>
  );
};
